import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { Loader2 } from 'lucide-react';
import { prefetchAuthenticatedRoute } from 'components/AuthenticatedRoutes/authenticatedRoutePrefetch';
import { useTranslation } from 'react-i18next';
import { useCommandPaletteBookReferenceSearch } from 'hooks/useCommandPaletteBookReferenceSearch';
import { useCommandPaletteDirectedSearch } from 'hooks/useCommandPaletteDirectedSearch';
import { commandPaletteItems } from 'utils/commandPaletteItems';
import { LocalizedCommandPaletteItem } from 'types/commandPalette';
import type { CommandPaletteBookReferenceResult } from 'types/commandPaletteBookReference';
import type { CommandPaletteDirectedSearchResult } from 'types/commandPaletteDirectedSearch';
import {
  useActiveCompany,
  useActiveCompanyStore,
} from 'stores/activeCompanyStore';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { useCompanyInstalledApps } from 'hooks/useCompanyInstalledApps';
import { isAllCompaniesSelectionActive } from 'utils/allCompaniesMode';
import { ensureAliasForCompanyId } from 'utils/activeCompanyStorageUtils';
import { ROUTES, URL_PARAM_KEYS } from 'utils/constants';
import { getDownloadablesVisibility } from 'utils/downloadablesVisibility';
import { isPathAllowedForInstalledApps } from 'utils/installedAppPathScope';
import { getCommandPaletteDirectedSearchKindLabelKey } from 'utils/commandPaletteDirectedSearchLabels';
import SearchOverlay from 'components/SearchOverlay';
import { useCategoryInit } from 'stores/categoryStore';
import styles from './CommandPalette.module.scss';

const SECTION_ORDER = [
  'commandPalette.sections.bookReferences',
  'commandPalette.sections.accounts',
  'commandPalette.sections.contacts',
  'commandPalette.sections.emailMatches',
  'commandPalette.sections.pages',
  'commandPalette.sections.actions',
  'commandPalette.sections.reports',
  'commandPalette.sections.account',
] as const;

type CommandPaletteNavResult = {
  type: 'nav';
  item: LocalizedCommandPaletteItem;
};

type CommandPaletteBookReferenceRow = {
  type: 'bookReference';
  item: CommandPaletteBookReferenceResult;
};

type CommandPaletteDirectedSearchRow = {
  type: 'directedSearch';
  item: CommandPaletteDirectedSearchResult;
};

type CommandPaletteResultRow =
  | CommandPaletteNavResult
  | CommandPaletteBookReferenceRow
  | CommandPaletteDirectedSearchRow;

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  useCategoryInit();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const activeCompany = useActiveCompany();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const availableCompanies = useActiveCompanyStore((s) => s.availableCompanies);
  const { canAccessPath } = useUserPermissions();
  const { isAppInstalled, status: installedAppsStatus } =
    useCompanyInstalledApps();
  const showDownloadablesNav = getDownloadablesVisibility(
    isAppInstalled,
    installedAppsStatus === 'success',
  ).hasVisibleDownloads;
  const { t } = useTranslation();

  const buildBookReferenceHref = useCallback(
    (path: string) => {
      if (!activeCompanyId || isAllCompaniesSelectionActive(activeCompanyId)) {
        return path;
      }
      const orderedCompanyIds = availableCompanies.map((company) => company.id);
      if (!orderedCompanyIds.includes(activeCompanyId)) {
        return path;
      }
      const alias = ensureAliasForCompanyId(activeCompanyId, orderedCompanyIds);
      const joiner = path.includes('?') ? '&' : '?';
      return `${path}${joiner}${URL_PARAM_KEYS.COMPANY_ALIAS}=${encodeURIComponent(alias)}`;
    },
    [activeCompanyId, availableCompanies],
  );

  const visibleItems = useMemo(
    () =>
      commandPaletteItems.filter((item) => {
        if (item.ownerOnly && !activeCompany?.owned) {
          return false;
        }
        if (item.path === ROUTES.DOWNLOADABLES && !showDownloadablesNav) {
          return false;
        }
        if (!isPathAllowedForInstalledApps(item.path, isAppInstalled)) {
          return false;
        }
        return canAccessPath(item.path);
      }),
    [activeCompany?.owned, canAccessPath, isAppInstalled, showDownloadablesNav],
  );

  const localizedItems: LocalizedCommandPaletteItem[] = useMemo(
    () =>
      visibleItems.map((item) => ({
        ...item,
        title: t(item.titleKey),
        section: t(item.sectionKey),
      })),
    [visibleItems, t],
  );

  const fuse = useMemo(
    () =>
      new Fuse(localizedItems, {
        keys: [
          { name: 'title', weight: 0.6 },
          { name: 'keywords', weight: 0.3 },
          { name: 'section', weight: 0.1 },
        ],
        threshold: 0.4,
        includeScore: true,
        minMatchCharLength: 1,
      }),
    [localizedItems],
  );

  const {
    parsedQuery: bookReferenceParsedQuery,
    results: bookReferenceResults,
    status: bookReferenceStatus,
    awaitingDigits,
  } = useCommandPaletteBookReferenceSearch(query, isOpen, canAccessPath);

  const {
    parsedQuery: directedParsedQuery,
    awaitingHint: directedAwaitingHint,
    results: directedSearchResults,
    status: directedSearchStatus,
  } = useCommandPaletteDirectedSearch(query, isOpen, canAccessPath);

  const suppressNavSearch =
    bookReferenceParsedQuery !== null ||
    directedParsedQuery !== null ||
    directedAwaitingHint !== null;

  const navResults: LocalizedCommandPaletteItem[] = useMemo(() => {
    if (!query.trim() || suppressNavSearch) {
      return query.trim() && suppressNavSearch ? [] : localizedItems;
    }
    return fuse.search(query).map((result) => result.item);
  }, [fuse, localizedItems, query, suppressNavSearch]);

  const groupedResults = useMemo(() => {
    const groups: Record<string, CommandPaletteResultRow[]> = {};

    if (bookReferenceParsedQuery) {
      groups['commandPalette.sections.bookReferences'] =
        bookReferenceResults.map((item) => ({
          type: 'bookReference',
          item,
        }));
    }

    if (directedParsedQuery) {
      for (const item of directedSearchResults) {
        if (!groups[item.sectionKey]) {
          groups[item.sectionKey] = [];
        }
        groups[item.sectionKey].push({ type: 'directedSearch', item });
      }
    }

    for (const item of navResults) {
      if (!groups[item.sectionKey]) {
        groups[item.sectionKey] = [];
      }
      groups[item.sectionKey].push({ type: 'nav', item });
    }

    return SECTION_ORDER.filter((sectionKey) => groups[sectionKey]?.length).map(
      (sectionKey) => ({
        sectionKey,
        sectionLabel: t(sectionKey),
        items: groups[sectionKey],
      }),
    );
  }, [
    bookReferenceParsedQuery,
    bookReferenceResults,
    directedParsedQuery,
    directedSearchResults,
    navResults,
    t,
  ]);

  const flatResults = useMemo(
    () => groupedResults.flatMap((group) => group.items),
    [groupedResults],
  );

  const resolveRowPath = useCallback(
    (row: CommandPaletteResultRow) => {
      if (row.type === 'nav') {
        return row.item.path;
      }
      return buildBookReferenceHref(row.item.path);
    },
    [buildBookReferenceHref],
  );

  const selectRow = useCallback(
    (row: CommandPaletteResultRow) => {
      navigate(resolveRowPath(row));
      onClose();
    },
    [navigate, onClose, resolveRowPath],
  );

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const activeEl = listRef.current?.querySelector(
      `[data-index="${activeIndex}"]`,
    );
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    const activeRow = flatResults[activeIndex];
    const path = activeRow ? resolveRowPath(activeRow) : undefined;
    if (path) {
      prefetchAuthenticatedRoute(path);
    }
  }, [activeIndex, flatResults, resolveRowPath]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (flatResults.length === 0) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((index) => (index + 1) % flatResults.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex(
            (index) => (index - 1 + flatResults.length) % flatResults.length,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[activeIndex]) {
            selectRow(flatResults[activeIndex]);
          }
          break;
      }
    },
    [activeIndex, flatResults, selectRow],
  );

  let runningIndex = 0;
  const showBookReferenceHint =
    bookReferenceParsedQuery !== null &&
    (awaitingDigits ||
      bookReferenceStatus === 'loading' ||
      (bookReferenceStatus === 'success' &&
        bookReferenceResults.length === 0) ||
      bookReferenceStatus === 'error');

  const showDirectedSearchHint =
    directedAwaitingHint !== null ||
    (directedParsedQuery !== null &&
      (directedSearchStatus === 'loading' ||
        (directedSearchStatus === 'success' &&
          directedSearchResults.length === 0) ||
        directedSearchStatus === 'error'));

  const showSearchHint = showBookReferenceHint || showDirectedSearchHint;

  return (
    <SearchOverlay
      isOpen={isOpen}
      onClose={onClose}
      useHistory={false}
      query={query}
      onQueryChange={setQuery}
      placeholder={t('commandPalette.placeholder')}
      shortcutHint="ESC"
      onKeyDown={handleKeyDown}
    >
      <div ref={listRef} className={styles.resultsList}>
        {showSearchHint && flatResults.length === 0 ? (
          <div className={styles.hint}>
            {awaitingDigits
              ? t('commandPalette.bookReference.awaitingDigits', {
                  prefix: bookReferenceParsedQuery?.prefix ?? '',
                })
              : directedAwaitingHint
                ? t('commandPalette.directed.awaitingSearchText', {
                    prefix: directedAwaitingHint,
                  })
                : showBookReferenceHint && bookReferenceStatus === 'loading'
                  ? t('commandPalette.bookReference.searching')
                  : showDirectedSearchHint && directedSearchStatus === 'loading'
                    ? t('commandPalette.directed.searching')
                    : showBookReferenceHint && bookReferenceStatus === 'error'
                      ? t('commandPalette.bookReference.searchFailed')
                      : showDirectedSearchHint &&
                          directedSearchStatus === 'error'
                        ? t('commandPalette.directed.searchFailed')
                        : showBookReferenceHint
                          ? t('commandPalette.bookReference.noResults', {
                              query:
                                bookReferenceParsedQuery?.normalizedQuery ??
                                query.trim(),
                            })
                          : t('commandPalette.directed.noResults', {
                              query: query.trim(),
                            })}
          </div>
        ) : null}

        {flatResults.length === 0 && !showSearchHint ? (
          <div className={styles.empty}>{t('commandPalette.empty')}</div>
        ) : (
          groupedResults.map((group) => {
            const sectionItems = group.items.map((row) => {
              const idx = runningIndex++;
              const isActive = idx === activeIndex;

              if (
                row.type === 'bookReference' ||
                row.type === 'directedSearch'
              ) {
                const Icon = row.item.icon;
                const directedKindLabelKey =
                  row.type === 'directedSearch'
                    ? getCommandPaletteDirectedSearchKindLabelKey(row.item.kind)
                    : null;
                const directedKindLabel = directedKindLabelKey
                  ? t(directedKindLabelKey)
                  : null;
                const isLoading =
                  (row.type === 'bookReference' &&
                    bookReferenceStatus === 'loading' &&
                    group.sectionKey ===
                      'commandPalette.sections.bookReferences') ||
                  (row.type === 'directedSearch' &&
                    directedSearchStatus === 'loading' &&
                    (group.sectionKey === 'commandPalette.sections.accounts' ||
                      group.sectionKey === 'commandPalette.sections.contacts' ||
                      group.sectionKey ===
                        'commandPalette.sections.emailMatches'));
                return (
                  <Link
                    key={row.item.id}
                    data-index={idx}
                    className={`${styles.resultItem} ${isActive ? styles.active : ''}`}
                    to={buildBookReferenceHref(row.item.path)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => onClose()}
                  >
                    <Icon size={18} className={styles.resultIcon} />
                    <span className={styles.resultContent}>
                      <span className={styles.resultTitleRow}>
                        {directedKindLabel ? (
                          <span className={styles.resultKindBadge}>
                            {directedKindLabel}
                          </span>
                        ) : null}
                        <span className={styles.resultTitle}>
                          {row.item.title}
                        </span>
                      </span>
                      {row.item.subtitle ? (
                        <span className={styles.resultSubtitle}>
                          {row.item.subtitle}
                        </span>
                      ) : null}
                    </span>
                    {isLoading ? (
                      <Loader2
                        size={16}
                        className={styles.resultTrailingIcon}
                        aria-hidden
                      />
                    ) : null}
                  </Link>
                );
              }

              const Icon = row.item.icon;
              return (
                <button
                  key={row.item.id}
                  data-index={idx}
                  className={`${styles.resultItem} ${isActive ? styles.active : ''}`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => selectRow(row)}
                  type="button"
                >
                  <Icon size={18} className={styles.resultIcon} />
                  <span className={styles.resultTitle}>{row.item.title}</span>
                </button>
              );
            });

            return (
              <div key={group.sectionKey} className={styles.section}>
                <div className={styles.sectionLabel}>{group.sectionLabel}</div>
                {sectionItems}
              </div>
            );
          })
        )}
      </div>
    </SearchOverlay>
  );
}

export default CommandPalette;
